import React from 'react'
import { Portal } from 'react-portal'
import {
  Spring,
  animated,
  config,
  interpolate,
} from 'react-spring/renderprops.cjs'

import {
  compose,
  defaultProps,
  setDisplayName,
  withHandlers,
  withProps,
  withState,
} from 'recompose'
import styled from 'styled-components'

import { style } from 'theme'
import { MODAL_BODY, MODAL_CONTAINER } from 'utils/testing/testIDs'

import withAutoFocus, { AutoFocusProps } from '../HOCs/withAutoFocus'
import withClickOutside from '../HOCs/withClickOutside'
import withEscClose from '../HOCs/withEscClose'

type Props = {
  autoFocus?: boolean
  children: any
  className?: string
  closeOnOutsideClick: boolean
  closeOnEsc: boolean
  isOpen: boolean
  contentContainerStyle?: object
  'data-testid'?: string
  onRequestClose?: () => void
  onCloseAnimationComplete?: () => void
  onOpenAnimationComplete?: () => void
  withAnimationEnd?: () => void
}

type InnerProps = {
  onAnimationEnd: () => void
}

type HandlerProps = {
  handleRef: (ref: HTMLDivElement | null) => void
}

type PrivateProps = Props &
  HandlerProps &
  AutoFocusProps & {
    focusTarget: HTMLElement
    ref: HTMLElement
    onClickOutside: () => void
    setFocusTarget: (ref: HTMLElement) => void
  }

const Container = styled(animated.div)`
  display: flex;
  align-items: center;
  justify-content: center;

  position: fixed;
  top: 0;
  left: 0;

  width: 100vw;
  height: 100vh;

  will-change: opacity, transform;
  background: rgba(54, 63, 65, 0.6);
  z-index: 100;
`

export const Body = styled(animated.div)`
  width: calc(100% - 100px);
  max-height: 80vh;
  max-width: 420px;
  padding: 50px 40px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  border-radius: ${style((theme) => theme.styles.borderRadius)};
  background-color: #ffffff;
  box-shadow: ${style((theme) => theme.shadows.lgDark)};

  will-change: transform;
  transition: all 0.2s ease-out;
  position: relative;
  overflow: hidden;

  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    width: calc(100% - 40px);
    transition: none;
  }
`

const enhance = compose<PrivateProps & InnerProps, Props>(
  withProps((props: PrivateProps) => ({
    shouldFocus: props.autoFocus && props.isOpen,
  })),

  withAutoFocus,

  defaultProps({
    contentContainerStyle: {},
    onOpenAnimationComplete: () => null,
    onCloseAnimationComplete: () => null,
  }),

  withState('focusTarget', 'setFocusTarget', null),
  withHandlers<PrivateProps, HandlerProps>({
    handleRef:
      ({ focusTarget, setFocusTarget }) =>
      (ref: any) => {
        if (!focusTarget && ref) {
          setFocusTarget(ref.node || ref)
        }
      },
  }),
  withClickOutside(({ closeOnOutsideClick, onRequestClose, isOpen }: Props) => {
    if (closeOnOutsideClick && onRequestClose && isOpen) {
      onRequestClose()
    }
  }),
  withEscClose(({ closeOnEsc, onRequestClose }: Props) => {
    if (closeOnEsc && onRequestClose) {
      onRequestClose()
    }
  }),
  withHandlers<Props, { onAnimationEnd: () => void }>({
    onAnimationEnd: (props) => () => {
      if (props.isOpen) {
        // AFW-1677: Workaround for mobile Safari
        const element = document.activeElement as HTMLElement

        if (element && element.blur) {
          element.blur()
        }

        props.onOpenAnimationComplete?.()
      } else {
        props.onCloseAnimationComplete?.()
      }
    },
  }),
  setDisplayName('Modal'),
)

export default enhance(
  ({
    children,
    className,
    handleRef,
    isOpen,
    onRequestClose,
    onAnimationEnd,
    contentContainerStyle,
    ['data-testid']: testId,
  }) => (
    <Portal>
      <Spring
        native
        from={{ opacity: 0, scale: 0.5 }}
        to={{ opacity: isOpen ? 1 : 0, scale: isOpen ? 1 : 0.5 }}
        config={isOpen ? config.wobbly : config.gentle}
        onRest={onAnimationEnd}
      >
        {({ opacity, scale }) => (
          <Container
            data-testid={MODAL_CONTAINER}
            aria-hidden={!isOpen}
            aria-live="assertive"
            style={{
              opacity,
              pointerEvents: isOpen ? 'auto' : 'none',
            }}
          >
            <Body
              className={className}
              style={{
                transform: interpolate([scale], (s) => `scale(${s})`),
                pointerEvents: isOpen ? 'auto' : 'none',
                ...contentContainerStyle,
              }}
              ref={handleRef}
              data-testid={testId || MODAL_BODY}
            >
              {children({ exit: onRequestClose })}
            </Body>
          </Container>
        )}
      </Spring>
    </Portal>
  ),
) as React.ComponentClass<Props>
